import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, ChevronRight } from "lucide-react";
import { db } from "@/firebase/config";
import { collection, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import { StudentBehaviorProfile } from "./StudentBehaviorProfile";

interface StudentDirectoryProps {
  schoolId: string;
}

interface StudentSummary {
  id: string;
  firstName: string;
  lastName: string;
  registrationNumber: string;
  className: string;
  photoUrl?: string;
  behaviorTier?: number;
  behaviorScore?: number;
}

export const StudentDirectory = ({ schoolId }: StudentDirectoryProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);

  // Initial load
  useEffect(() => {
    if (!schoolId) return;
    loadStudents();
  }, [schoolId]);

  const loadStudents = async (term: string = "") => {
    setLoading(true);
    try {
        const studentsRef = collection(db, `schools/${schoolId}/students`);
        let q;
        
        if (term) {
             // Simplistic prefix search
             q = query(studentsRef, where("firstName", ">=", term), where("firstName", "<=", term + '\uf8ff'), limit(20));
        } else {
             // Default list (e.g., problematic students or just alphabetical)
             q = query(studentsRef, limit(20));
        }

        const snapshot = await getDocs(q);
        const results = snapshot.docs.map(doc => {
            const d = doc.data();
            return {
                id: doc.id,
                firstName: d.firstName,
                lastName: d.lastName,
                registrationNumber: d.registrationNumber,
                className: d.className || d.class || 'N/A',
                photoUrl: d.photoUrl,
                behaviorTier: d.behaviorTier || 1,
                behaviorScore: d.behaviorScore ?? 100
            };
        });
        setStudents(results);
    } catch (e) {
        console.error("Error loading students", e);
    } finally {
        setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
        if (searchQuery) loadStudents(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (selectedStudentId) {
      return (
          <StudentBehaviorProfile 
            schoolId={schoolId} 
            studentId={selectedStudentId} 
            onBack={() => setSelectedStudentId(null)} 
          />
      );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
          <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input 
                  placeholder="Search by name..." 
                  className="pl-9"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
              />
          </div>
          <Button variant="outline"><Filter className="h-4 w-4 mr-2" /> Filter</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
              <div className="col-span-full py-8 text-center text-muted-foreground">Loading directory...</div>
          ) : students.length === 0 ? (
              <div className="col-span-full py-8 text-center text-muted-foreground">No students found.</div>
          ) : (
              students.map(student => (
                  <div 
                    key={student.id} 
                    className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => setSelectedStudentId(student.id)}
                  >
                      <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {student.photoUrl ? <img src={student.photoUrl} className="h-full w-full object-cover" /> : <span className="font-bold text-slate-500">{student.firstName[0]}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{student.firstName} {student.lastName}</p>
                          <p className="text-xs text-muted-foreground truncate">{student.registrationNumber} • {student.className}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                          <div className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              (student.behaviorScore ?? 100) > 80 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                              {student.behaviorScore ?? 100}%
                          </div>
                          {student.behaviorTier && student.behaviorTier > 1 && (
                              <div className="text-[10px] font-bold text-red-600">Tier {student.behaviorTier}</div>
                          )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500" />
                  </div>
              ))
          )}
      </div>
    </div>
  );
};
